// ─────────────────────────────────────────
// Nav.js — shared navigation component
// ─────────────────────────────────────────
// Desktop: horizontal tab bar below the topbar (same as before)
// Mobile:  fixed bottom tab bar (like a native app)
// Active tab is auto-detected from the current URL — no props needed.

import { Link, useLocation } from 'react-router-dom'
import { useIsMobile } from '../hooks/useIsMobile'

const tabs = [
  { to: '/',            label: 'Feed',     icon: '🏠' },
  { to: '/chat',        label: 'Chat',     icon: '💬' },
  { to: '/groups',      label: 'Groups',   icon: '👥' },
  { to: '/leaderboard', label: 'Boards',   icon: '🏆' },
  { to: '/passport',    label: 'Passport', icon: '🗺️' },
]

function isActive(tab, pathname) {
  if (tab.to === '/') return pathname === '/'
  return pathname.startsWith(tab.to)
}

export default function Nav() {
  const location = useLocation()
  const isMobile = useIsMobile()

  if (isMobile) {
    return (
      <div style={mob.nav}>
        {tabs.map(tab => {
          const active = isActive(tab, location.pathname)
          return (
            <Link
              key={tab.to}
              to={tab.to}
              style={{ ...mob.tab, color: active ? '#c8f135' : 'rgba(255,255,255,0.45)' }}
            >
              <span style={mob.icon}>{tab.icon}</span>
              <span style={mob.label}>{tab.label}</span>
            </Link>
          )
        })}
      </div>
    )
  }

  // Desktop — same horizontal nav as before
  return (
    <div style={desk.nav}>
      {tabs.map(tab => {
        const active = isActive(tab, location.pathname)
        return (
          <Link
            key={tab.to}
            to={tab.to}
            style={{ ...desk.navItem, ...(active ? desk.navActive : {}) }}
          >
            {tab.icon} {tab.label}
          </Link>
        )
      })}
    </div>
  )
}

// ── Mobile styles ─────────────────────────────────────────────
const mob = {
  nav: {
    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
    background: '#0e1f13',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    // paddingBottom respects iPhone home-bar / notch area
    paddingBottom: 'env(safe-area-inset-bottom)',
  },
  tab: {
    flex: 1,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    padding: '10px 4px 8px',
    textDecoration: 'none', gap: 3,
  },
  icon: { fontSize: 22, lineHeight: 1 },
  label: {
    fontSize: 9, fontWeight: 700, letterSpacing: 0.4,
    fontFamily: "'Barlow Condensed', sans-serif",
    textTransform: 'uppercase',
  },
}

// ── Desktop styles ────────────────────────────────────────────
const desk = {
  nav: {
    background: '#0e1f13', borderTop: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', padding: '0 20px 10px',
  },
  navItem: {
    color: 'rgba(255,255,255,0.4)', textDecoration: 'none', fontSize: 12,
    fontWeight: 700, padding: '8px 14px', borderRadius: 20, letterSpacing: 0.5,
  },
  navActive: { background: 'rgba(200,241,53,0.12)', color: '#c8f135' },
}
